import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, MapPin } from 'lucide-react';

// Location control in the reference's visual grammar: a bordered select-like
// button that reads "Anywhere" until a city is set, opening a small panel with
// a free-text city/coordinates input. It stays free text on purpose: geocoding
// runs ONCE per search at submit time (Nominatim's usage policy forbids
// autocomplete), so this component is a presentation shell over formData.city.
const LocationSelect = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onDocMouseDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    inputRef.current?.focus();
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const hasCity = Boolean((value || '').trim());

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="fsc-city-panel"
        aria-label={`Location: ${hasCity ? value : 'Anywhere'}`}
        className="serp-select-btn"
      >
        <MapPin aria-hidden="true" className="w-[18px] h-[18px] text-slate-400 flex-none" />
        <span className={`flex-1 truncate text-left ${hasCity ? 'text-slate-800' : 'text-slate-500'}`}>
          {hasCity ? value : 'Anywhere'}
        </span>
        <ChevronDown aria-hidden="true" className="w-4 h-4 text-slate-400 flex-none" />
      </button>

      {open && (
        // Disclosure popover, NOT a dialog: focus is not trapped and the rest
        // of the page stays interactive, so no dialog role is claimed.
        <div
          id="fsc-city-panel"
          className="absolute z-40 mt-1 w-full min-w-[240px] rounded border border-[#dadee5] bg-white p-3 shadow-lg"
        >
          <label htmlFor="fsc-city" className="block text-xs font-medium text-slate-500 mb-1.5 text-left">
            City, area or “lat, lng” (optional)
          </label>
          <input
            ref={inputRef}
            id="fsc-city"
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              // Enter falls through to the form submit; just fold the panel.
              if (e.key === 'Enter') setOpen(false);
            }}
            placeholder="e.g. Madrid"
            className="serp-input px-3.5"
          />
          <p className="mt-1.5 text-[11px] leading-4 text-slate-500 text-left">
            Located automatically when you search. Empty = country-wide SERP.
          </p>
          <div className="mt-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                onChange('');
                setOpen(false);
                buttonRef.current?.focus();
              }}
              className="text-sm font-medium text-slate-500 hover:text-slate-700 min-h-[36px] px-2"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                buttonRef.current?.focus();
              }}
              className="text-sm font-medium text-brand-600 hover:text-brand-700 min-h-[36px] px-2"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSelect;
